import { CaretDown, CircleNotch } from "@phosphor-icons/react";
import { useMemo, useState } from "react";

import { ReviewListCard } from "./ReviewListCard";

import { useChatReviewSummaries } from "../hooks/useChatReviewSummaries";

/**
 * Right-hand REVIEWS rail for standalone Ask Puntt chats with multiple review
 * tickets. Mirrors the project `ProjectReviewsPanel` chrome but is driven by
 * ticket ids collected from the chat transcript.
 */
export function ChatReviewsPanel({
  ticketIds,
  titleMap,
  onOpenReview,
  status,
  open: openProp,
  onOpenChange,
}: {
  ticketIds: readonly string[];
  titleMap: Map<string, string>;
  onOpenReview: (ticketId: string) => void;
  /** Overall reviews status chip (e.g. COMPLETED, IN REVIEW). */
  status?: string;
  /** Controlled expand state for the reviews section header. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [openInternal, setOpenInternal] = useState(true);
  const open = openProp ?? openInternal;
  const setOpen = (next: boolean) => {
    onOpenChange?.(next);
    if (openProp === undefined) setOpenInternal(next);
  };

  const stableTitleMap = useMemo(() => titleMap, [titleMap]);
  const { reviews, isLoading } = useChatReviewSummaries(
    ticketIds,
    stableTitleMap,
  );

  if (ticketIds.length <= 1) return null;

  return (
    <section
      className={`flex min-h-0 flex-col overflow-hidden rounded-xl border border-puntt-neutral-gray-5 bg-base-white ${open ? "h-full flex-1" : "shrink-0"}`}
      aria-label="Reviews"
      data-testid="chat-reviews-panel"
      data-panel-expanded={open ? "true" : "false"}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-label="Toggle reviews section"
        data-testid="chat-reviews-toggle"
        className="flex w-full shrink-0 items-center gap-2 bg-base-white px-4 py-3 text-left"
      >
        <h2 className="flex-1 text-[11px] font-bold uppercase tracking-wide text-puntt-neutral-gray-12">
          Reviews
        </h2>
        <CaretDown
          size={14}
          aria-hidden="true"
          className={`shrink-0 text-puntt-neutral-gray-11 transition-transform ${open ? "" : "-rotate-90"}`}
        />
      </button>

      {open && (
        <div
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain border-t border-puntt-neutral-gray-5 px-4 pb-4 pt-3 [scrollbar-width:thin]"
          data-testid="chat-reviews-scroll"
        >
          {status ? (
            <p
              className="text-puntt-yellow-11 mb-3 text-[11px] font-bold uppercase tracking-wide"
              data-testid="chat-reviews-status"
            >
              {status}
            </p>
          ) : null}

          {isLoading ? (
            <div
              className="flex items-center justify-center py-4"
              data-testid="chat-reviews-loading"
            >
              <CircleNotch
                size={18}
                weight="bold"
                className="animate-spin text-puntt-neutral-gray-9"
              />
            </div>
          ) : reviews.length === 0 ? (
            <p
              className="text-[13px] leading-relaxed text-puntt-neutral-gray-10"
              data-testid="chat-reviews-empty"
            >
              No reviews yet. Reviews appear here when artwork reviews complete
              in this chat.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {reviews.map((item) => (
                <ReviewListCard
                  key={item.id}
                  data-testid={`chat-review-card-${item.id}`}
                  item={{
                    id: item.id,
                    name: item.name,
                    versionNumber: item.versionNumber,
                    thumbnailUrl: item.thumbnailUrl,
                    aiCommentCount: item.aiCommentCount,
                    humanCommentCount: item.humanCommentCount,
                  }}
                  testIdPrefix="chat-review-card"
                  onClick={() => onOpenReview(item.ticketId)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
